import {Component, computed, inject, OnInit, signal, ChangeDetectionStrategy} from "@angular/core"
import {ActivatedRoute, ParamMap, Router} from "@angular/router"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {AuthService} from "@common/services/auth/auth.service"
import {LogInViaSsoGQL, VerifyExternalLoginGQL} from "@common/components/handle-sso-callback/handle-sso-callback.generated"
import {FullPageFeedbackComponent} from "@common/components/full-page-feedback/full-page-feedback.component"
import {isSafeReturnUrl} from "@common/helpers/safe-redirect/safe-redirect"
import {LoginProvider} from "@generated"
import {z} from "zod"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-handle-sso-callback",
    imports: [FullPageFeedbackComponent],
    templateUrl: "./handle-sso-callback.component.html",
    styleUrl: "./handle-sso-callback.component.scss",
})
export class HandleSsoCallbackComponent implements OnInit {
    readonly auth = inject(AuthService)
    readonly activatedRoute = inject(ActivatedRoute)
    readonly notifications = inject(NotificationsService)
    readonly router = inject(Router)
    readonly verifyExternalLogin = inject(VerifyExternalLoginGQL)
    readonly logInViaSso = inject(LogInViaSsoGQL)

    readonly $provider = signal<"google" | "microsoft" | "richemont" | undefined>(undefined)

    // The full-page feedback component has no Richemont icon; fall back to a generic one.
    readonly $feedbackIcon = computed<"google" | "microsoft" | "id">(() => {
        switch (this.$provider()) {
            case "google":
                return "google"
            case "microsoft":
                return "microsoft"
            case "richemont":
                return "id"
            case undefined:
                return "google"
        }
    })

    ngOnInit() {
        // using the fragment for Microsoft, query params for Google
        // TODO: unify this and include provider in the state to make it more robust
        const parsedData = z
            .object({provider: z.literal("google").or(z.literal("microsoft")).or(z.literal("richemont"))})
            .safeParse(this.activatedRoute.snapshot.data)
        if (!parsedData.success) {
            this.notifications.showError("Invalid login provider data")
            void this.router.navigateByUrl("/login")
            return
        }
        this.$provider.set(parsedData.data.provider)
        switch (this.$provider()) {
            case "google":
                void this.performLogin(LoginProvider.Google, this.activatedRoute.snapshot.queryParamMap)
                break
            case "microsoft": {
                const paramMap = new URLSearchParams(this.activatedRoute.snapshot.fragment ?? "")
                void this.performLogin(LoginProvider.Microsoft, paramMap)
                break
            }
            case "richemont":
                void this.performRichemontLogin(this.activatedRoute.snapshot.queryParamMap)
        }
    }

    private performRichemontLogin = async (paramMap: ParamMap) => {
        const error = paramMap.get("error_description") ?? paramMap.get("error")
        if (error) {
            this.notifications.showError(error)
            await this.router.navigateByUrl("/login")
            return
        }
        const code = paramMap.get("code")
        const state = paramMap.get("state")
        if (!code || !state) {
            this.notifications.showError("Missing SSO response parameters")
            await this.router.navigateByUrl("/login")
            return
        }
        // The state is an opaque single-use value validated server-side; no client-side
        // CSRF/state parsing is needed for this flow.
        const result = await this.notifications.mutateWithUserFeedback(this.logInViaSso, {code, state}, {})
        if (!result) {
            await this.router.navigateByUrl("/login")
            return
        }
        const success = await this.auth.completeLogin(result.logInViaSso)
        if (success) {
            await this.auth.navigateToDefaultView()
        } else {
            this.notifications.showError("Failed to complete login process")
            await this.router.navigateByUrl("/login")
        }
    }

    private performLogin = async (provider: LoginProvider, paramMap: ParamMap | URLSearchParams) => {
        const error = paramMap.get("error_description") ?? paramMap.get("error")
        if (error) {
            this.notifications.showError(error)
            await this.router.navigateByUrl("/login")
            return
        }
        const state = paramMap.get("state")
        if (!state) {
            this.notifications.showError("Missing state parameter")
            await this.router.navigateByUrl("/login")
            return
        }
        const stateData = z.object({csrfToken: z.string(), redirectUri: z.string().optional()}).safeParse(JSON.parse(atob(state)))
        if (!stateData.success) {
            this.notifications.showError("Invalid response from login provider")
            await this.router.navigateByUrl("/login")
            return
        }
        if (stateData.data.csrfToken !== this.auth.csrfToken) {
            this.notifications.showError("Invalid CSRF token")
            await this.router.navigateByUrl("/login")
            return
        }
        const code = paramMap.get("code") ?? paramMap.get("id_token")
        if (!code) {
            this.notifications.showError("No code found")
            await this.router.navigateByUrl("/login")
            return
        }
        const result = await this.notifications.mutateWithUserFeedback(this.verifyExternalLogin, {code, provider}, {})
        if (result) {
            const {verifyExternalLogin: loginInfo} = result
            const success = await this.auth.completeLogin(loginInfo)
            if (success) {
                if (isSafeReturnUrl(stateData.data.redirectUri)) {
                    await this.router.navigateByUrl(stateData.data.redirectUri)
                } else {
                    await this.auth.navigateToDefaultView()
                }
            } else {
                this.notifications.showError("Failed to complete login process")
                await this.router.navigateByUrl("/login")
                return
            }
        } else {
            await this.router.navigateByUrl("/login")
            return
        }
    }
}
